import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConventionDatesControl } from 'app/models/convention-dates-control.model';
import { ConventionMailingInformation } from 'app/models/convention-mailing-information.model';
import { ConventionOfficerSignature } from 'app/models/convention-officer-signature.model';
import { ConventionRegistration } from 'app/models/convention-registration.model';
import { CredentialGenerator } from 'app/models/credential-generator.model';
import { DailyRegister } from 'app/models/daily-register.model';
import { DeleteConventionRegistrationHistory } from 'app/models/delete-convention-registration-history.model';
import { Event } from 'app/models/event.model';
import { ExceptionList } from 'app/models/exception-list.model';
import { EventConventionExtension } from 'app/models/extensions/event-convention-extension.model';
import { LinkAttendeeTypeConvention } from 'app/models/link-attendee-type-convention.model';
import { LinkGenderConvention } from 'app/models/link-gender-convention.model';
import { LinkHEUConvention } from 'app/models/link-h-e-u-convention.model';
import { LinkLanguageConvention } from 'app/models/link-language-convention.model';
import { LinkMinorityGroupConvention } from 'app/models/link-minority-group-convention.model';
import { LinkNotQualifyReasonConvention } from 'app/models/link-not-qualify-reason-convention.model';
import { LOVConventionType } from 'app/models/lov-convention-type.model';
import { OrganizationList } from 'app/models/organization-list.model';
import { PaymentFeeType } from 'app/models/payment-fee-type.model';
import { ProcessedEntity } from 'app/models/processed-entity.model';
import { ReportHeaderInfo } from 'app/models/report-header-info.model';
import { SectoralMeeting } from 'app/models/sectoral-meeting.model';
import { MRMValidators } from '@mrm/core';

export class EventConvention extends EventConventionExtension {
  eventConventionDBID: number = 0;
  conventionNumber: string = null;
  conventionTypeDBID: number = 0;
  glAccountNumber: string = null;
  conventionDatesControlDBID: number = 0;
  conventionFee: number = 0;
  sectoralMeetingFee: number = 0;
  numberOfAlternateAllowed: number = 0;
  numberOfAssistFundDelegatesAllowed: number = 0;
  previousConventionDBID?: number = null;
  conventionMailingInformationDBID: number = 0;
  completed: boolean = false;
  noteDBID?: number = null;
  glLastExportDT?: Date = null;
  attendeesLastUpdatedDT?: Date = null;
  pctLoadLastUpdateDT?: Date = null;
  localExceptionLastRevisedYear?: Date = null;
  organizationIncludeLastRevisedYear?: Date = null;
  createdDT: Date = new Date();
  createdBy: string = null;
  modifiedDT: Date = new Date();
  modifiedBy: string = null;
  rowVersion: number = 0;
  conventionDatesControl: ConventionDatesControl = null;
  conventionMailingInformation: ConventionMailingInformation = null;
  conventionType: LOVConventionType = null;
  isVirtual: boolean = false;
  conventionOfficerSignatures: ConventionOfficerSignature[] = [];
  conventionRegistrations: ConventionRegistration[] = [];
  credentialGenerators: CredentialGenerator[] = [];
  dailyRegisters: DailyRegister[] = [];
  deleteConventionRegistrationHistories: DeleteConventionRegistrationHistory[] = [];
  events: Event[] = [];
  exceptionLists: ExceptionList[] = [];
  linkAttendeeTypeConventions: LinkAttendeeTypeConvention[] = [];
  linkGenderConventions: LinkGenderConvention[] = [];
  linkHEUConventions: LinkHEUConvention[] = [];
  linkLanguageConventions: LinkLanguageConvention[] = [];
  linkMinorityGroupConventions: LinkMinorityGroupConvention[] = [];
  linkNotQualifyReasonConventions: LinkNotQualifyReasonConvention[] = [];
  organizationLists: OrganizationList[] = [];
  paymentFeeTypes: PaymentFeeType[] = [];
  processedEntities: ProcessedEntity[] = [];
  reportHeaderInfos: ReportHeaderInfo[] = [];
  sectoralMeetings: SectoralMeeting[] = [];

  static getForm(disableAll: boolean = false, includeNav: string[] = []): FormGroup {
    const form = new FormGroup({
      eventConventionDBID: new FormControl({ value: 0, disabled: disableAll }),
      conventionNumber: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(3)]),
      conventionTypeDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      glAccountNumber: new FormControl({ value: null, disabled: disableAll }, [Validators.required, Validators.maxLength(50)]),
      conventionDatesControlDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      conventionFee: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.money]),
      sectoralMeetingFee: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.money]),
      numberOfAlternateAllowed: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      numberOfAssistFundDelegatesAllowed: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      previousConventionDBID: new FormControl({ value: null, disabled: disableAll }),
      conventionMailingInformationDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      completed: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      noteDBID: new FormControl({ value: null, disabled: disableAll }),
      glLastExportDT: new FormControl({ value: null, disabled: disableAll }),
      attendeesLastUpdatedDT: new FormControl({ value: null, disabled: disableAll }),
      pctLoadLastUpdateDT: new FormControl({ value: null, disabled: disableAll }),
      localExceptionLastRevisedYear: new FormControl({ value: null, disabled: disableAll }),
      organizationIncludeLastRevisedYear: new FormControl({ value: null, disabled: disableAll }),
      createdDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      createdBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      modifiedDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      modifiedBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      rowVersion: new FormControl({ value: 0, disabled: disableAll }),
      isVirtual: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      conventionOfficerSignatures: new FormArray([]),
      conventionRegistrations: new FormArray([]),
      credentialGenerators: new FormArray([]),
      dailyRegisters: new FormArray([]),
      deleteConventionRegistrationHistories: new FormArray([]),
      events: new FormArray([]),
      exceptionLists: new FormArray([]),
      linkAttendeeTypeConventions: new FormArray([]),
      linkGenderConventions: new FormArray([]),
      linkHEUConventions: new FormArray([]),
      linkLanguageConventions: new FormArray([]),
      linkMinorityGroupConventions: new FormArray([]),
      linkNotQualifyReasonConventions: new FormArray([]),
      organizationLists: new FormArray([]),
      paymentFeeTypes: new FormArray([]),
      processedEntities: new FormArray([]),
      reportHeaderInfos: new FormArray([]),
      sectoralMeetings: new FormArray([])
    });

    // Only add navigational properties to the form when needed.
    // If navigational properties are included they MUST NOT be null when patching the form.
    if (includeNav.length > 0) {
      for (const key of includeNav) {
        switch (key.toLowerCase()) {
          case 'conventiondatescontrol':
            form.addControl('conventionDatesControl', ConventionDatesControl.getForm(disableAll)); break;
          case 'conventionmailinginformation':
            form.addControl('conventionMailingInformation', ConventionMailingInformation.getForm(disableAll)); break;
          case 'conventiontype':
            form.addControl('conventionType', LOVConventionType.getForm(disableAll)); break;
          default:
            // tslint:disable-next-line:max-line-length
            throw new Error(`'${key}' is not a property of EventConvention. Please use one of the following navigational property: 'conventionDatesControl' | 'conventionMailingInformation' | 'conventionType'`);
        }
      }
    }

    return form;
  }
}